import { forwardRef, useState } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Badge, IconButton, Tooltip } from '@vnetwork/datacentral-ui/primitives'
import { cn } from '@/lib/utils'

/**
 * The one definition of a topbar icon button (alerts, notifications, what's new,
 * help, mobile search). It fixes everything that used to drift per button:
 *   - IconButton size `md` (34px box) and one 16px lucide glyph, centred
 *   - one colour rule: muted at rest, `danger` / `warning` / `primary` when it has news
 *   - the count badge: top-right, hanging off the corner so it never covers the
 *     glyph, and it is out of flow so it never moves the button or its neighbours
 *   - the status dot: bottom-right, same rule
 *   - one tooltip (the label), which stays shut while a menu on it is open
 *
 * Callers pass data, never classes: no per-button size, offset or colour.
 */
type TopbarActionTone = 'default' | 'danger' | 'warning' | 'primary'

interface TopbarActionBadge {
    count: number
    tone: 'danger' | 'warning' | 'primary'
}

interface TopbarActionDot {
    tone: 'danger' | 'warning'
    /** Accessible text for the dot (a title tooltip and an aria-label). */
    label?: string
}

interface TopbarActionProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    /** Accessible name. Also the tooltip unless `tooltip` is given. */
    'aria-label': string
    icon: LucideIcon
    /** Tooltip text when it differs from the accessible name; `null` for none. */
    tooltip?: ReactNode | null
    tone?: TopbarActionTone
    badge?: TopbarActionBadge
    dot?: TopbarActionDot
    /** Keep the tooltip shut (a menu is open on this button). */
    suppressTooltip?: boolean
}

const ICON_TONE: Record<TopbarActionTone, string> = {
    default: 'text-muted-foreground',
    danger: 'text-danger-text',
    warning: 'text-warning-text',
    primary: 'text-primary-text',
}

const DOT_TONE: Record<TopbarActionDot['tone'], string> = {
    danger: 'bg-danger',
    warning: 'bg-warning',
}

const TopbarAction = forwardRef<HTMLButtonElement, TopbarActionProps>(
    function TopbarAction(
        {
            'aria-label': ariaLabel,
            icon: Icon,
            tooltip,
            tone = 'default',
            badge,
            dot,
            suppressTooltip = false,
            className,
            ...rest
        },
        ref
    ) {
        // Controlled so a menu closing (focus returns to the button) cannot
        // reopen the tooltip on its own.
        const [tipOpen, setTipOpen] = useState(false)
        const tip = tooltip === undefined ? ariaLabel : tooltip

        return (
            <Tooltip
                content={tip}
                open={tipOpen && !suppressTooltip}
                onOpenChange={setTipOpen}
            >
                <IconButton
                    ref={ref}
                    variant="ghost"
                    aria-label={ariaLabel}
                    className={cn('relative', ICON_TONE[tone], className)}
                    {...rest}
                >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {badge && badge.count > 0 ? (
                        <Badge
                            size="sm"
                            appearance="solid"
                            tone={badge.tone}
                            aria-hidden="true"
                            className="pointer-events-none absolute -right-1.5 -top-2 h-4 min-w-4 justify-center rounded-full px-1"
                        >
                            {badge.count > 99 ? '99+' : badge.count}
                        </Badge>
                    ) : null}
                    {dot ? (
                        <span
                            role={dot.label ? 'img' : undefined}
                            aria-label={dot.label}
                            aria-hidden={dot.label ? undefined : true}
                            title={dot.label}
                            className={cn(
                                'pointer-events-none absolute bottom-px right-px h-2 w-2 rounded-full',
                                DOT_TONE[dot.tone]
                            )}
                        />
                    ) : null}
                </IconButton>
            </Tooltip>
        )
    }
)

export default TopbarAction
